import styled from 'styled-components';
import spacing from '../../styles/spacing';
import { vars } from '../../theme';
import { HBTagColor, HBTagSize, MemberTagColorTokens } from './HBTag.types';

export const colorMap: Record<HBTagColor, MemberTagColorTokens> = {
    neutral: {
        background: vars.colors.surface.itemInset,
        border: vars.colors.stroke.secondary,
        label: vars.colors.text.primary,
        hover: vars.colors.surface.itemInsetHover,
        pressed: vars.colors.surface.itemInsetPressed,
        disabledLabel: vars.colors.text.disabled,
        disabledBorder: vars.colors.stroke.disabled
    },
    red: {
        background: vars.colors.surface.red,
        border: vars.colors.stroke.redTertiary,
        label: vars.colors.text.red,
        hover: vars.colors.surface.redHover,
        pressed: vars.colors.surface.redPressed
    },
    orange: {
        background: vars.colors.surface.orange,
        border: vars.colors.stroke.orangeTertiary,
        label: vars.colors.text.orange,
        hover: vars.colors.surface.orangeHover,
        pressed: vars.colors.surface.orangePressed
    },
    yellow: {
        background: vars.colors.surface.yellow,
        border: vars.colors.stroke.yellowTertiary,
        label: vars.colors.text.yellow,
        hover: vars.colors.surface.yellowHover,
        pressed: vars.colors.surface.yellowPressed
    },
    khaki: {
        background: vars.colors.surface.khaki,
        border: vars.colors.stroke.khakiTertiary,
        label: vars.colors.text.khaki,
        hover: vars.colors.surface.khakiHover,
        pressed: vars.colors.surface.khakiPressed
    },
    green: {
        background: vars.colors.surface.green,
        border: vars.colors.stroke.greenTertiary,
        label: vars.colors.text.green,
        hover: vars.colors.surface.greenHover,
        pressed: vars.colors.surface.greenPressed
    },
    cyan: {
        background: vars.colors.surface.cyan,
        border: vars.colors.stroke.cyanTertiary,
        label: vars.colors.text.cyan,
        hover: vars.colors.surface.cyanHover,
        pressed: vars.colors.surface.cyanPressed
    },
    blue: {
        background: vars.colors.surface.blue,
        border: vars.colors.stroke.blueTertiary,
        label: vars.colors.text.blue,
        hover: vars.colors.surface.blueHover,
        pressed: vars.colors.surface.bluePressed
    },
    purple: {
        background: vars.colors.surface.purple,
        border: vars.colors.stroke.purpleTertiary,
        label: vars.colors.text.purple,
        hover: vars.colors.surface.purpleHover,
        pressed: vars.colors.surface.purplePressed
    },
    pink: {
        background: vars.colors.surface.pink,
        border: vars.colors.stroke.pinkTertiary,
        label: vars.colors.text.pink,
        hover: vars.colors.surface.pinkHover,
        pressed: vars.colors.surface.pinkPressed
    }
};

export const STRUCTURE: Record<
    HBTagSize,
    {
        height: string;
        textType: 'body14' | 'body16';
        padding: string;
        gap: string;
        borderRadius: string;
        innerBorderRadius: string;
    }
> = {
    sm: {
        height: spacing[24],
        textType: 'body14',
        padding: `0 ${spacing[6]}`,
        gap: spacing[6],
        borderRadius: spacing[6],
        innerBorderRadius: '5px'
    },
    md: {
        height: spacing[32],
        textType: 'body16',
        padding: `0 ${spacing[8]}`,
        gap: spacing[8],
        borderRadius: spacing[8],
        innerBorderRadius: '7px'
    }
};

export const PrefixWrapper = styled.span`
    color: inherit;
    display: inline-flex;
    align-items: center;
    line-height: 1;
`;

export const FOCUS_COLOR = vars.colors.stroke.keyboardFocus;

export interface ResolvedTagTokens {
    background: string;
    border: string;
    color: string;
    hover: string;
    pressed: string;
}

export interface ResolvedTagStructure {
    padding: string;
    gap: string;
    innerBorderRadius: string;
}

/*
 * Focus styles rationale:
 * - focusResetStyles resets color/background to prevent global button:focus overrides
 *   from changing the tag's appearance.
 * - interactiveStyles provides hover/active/focus-visible states including
 *   a visible keyboard-focus ring (FOCUS_COLOR outline) only when navigating
 *   via keyboard, keeping mouse interactions clean.
 */
const focusResetStyles = (tokens: ResolvedTagTokens) => `
    &:focus {
        color: ${tokens.color};
        background-color: ${tokens.background};
    }
`;

const interactiveStyles = (tokens: ResolvedTagTokens) => `
    cursor: pointer;

    &:hover, &[data-hover] {
        background-color: ${tokens.hover};
        color: ${tokens.color};
    }

    &:active, &[data-pressed] {
        background-color: ${tokens.pressed};
        color: ${tokens.color};
    }

    &:focus-visible, &[data-focus] {
        outline: 2px solid ${FOCUS_COLOR};
        outline-offset: 2px;
        background-color: ${tokens.hover};
        color: ${tokens.color};
        z-index: 1;
    }
`;

export const TagContainer = styled.div<{
    $border: string;
    $height: string;
    $borderRadius: string;
    $disabled: boolean;
    $disabledLink: boolean;
}>`
    display: inline-flex;
    align-items: center;
    height: ${({ $height }) => $height};
    border: 1px solid ${({ $border }) => $border};
    border-radius: ${({ $borderRadius }) => $borderRadius};

    ${({ $disabled }) => $disabled && `cursor: not-allowed;`}

    /* A disabled link must receive the click so the hook can stop it from reaching a parent row. */
    ${({ $disabled, $disabledLink }) =>
        $disabled && !$disabledLink && `pointer-events: none;`}
`;

export const TagContent = styled.div<{
    $tokens: ResolvedTagTokens;
    $structure: ResolvedTagStructure;
    $interactive: boolean;
    $focusableDisabled: boolean;
    $disabledOpacity: boolean;
}>`
    display: inline-flex;
    align-items: center;
    height: 100%;
    padding: ${({ $structure }) => $structure.padding};
    gap: ${({ $structure }) => $structure.gap};
    background-color: ${({ $tokens }) => $tokens.background};
    color: ${({ $tokens }) => $tokens.color};
    border: none;
    font: inherit;
    outline: none;
    /* For anchor elements — TagContent is a flex container, so the UA underline would
       otherwise paint through the label and the prefix. */
    text-decoration: none;
    border-radius: ${({ $structure }) => $structure.innerBorderRadius};

    ${({ $disabledOpacity }) => $disabledOpacity && `opacity: 0.5;`}

    ${({ $tokens }) => focusResetStyles($tokens)}

    &:not(:last-child) {
        border-radius: ${({ $structure }) => $structure.innerBorderRadius} 0 0
            ${({ $structure }) => $structure.innerBorderRadius};
    }

    ${({ $interactive, $tokens }) => $interactive && interactiveStyles($tokens)}

    ${({ $focusableDisabled }) =>
        $focusableDisabled &&
        `
        &:focus-visible, &[data-focus] {
            outline: 2px solid ${FOCUS_COLOR};
            outline-offset: 2px;
            z-index: 1;
        }
    `}
`;

export const DeleteButton = styled.button<{
    $tokens: ResolvedTagTokens;
    $innerBorderRadius: string;
    $disabled: boolean;
    $disabledOpacity: boolean;
}>`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    aspect-ratio: 1;
    background-color: ${({ $tokens }) => $tokens.background};
    color: ${({ $tokens }) => $tokens.color};
    border: none;
    border-left: 1px solid ${({ $tokens }) => $tokens.border};
    border-radius: 0 ${({ $innerBorderRadius }) => $innerBorderRadius}
        ${({ $innerBorderRadius }) => $innerBorderRadius} 0;

    ${({ $disabledOpacity }) => $disabledOpacity && `opacity: 0.5;`}

    ${({ $tokens }) => focusResetStyles($tokens)}

    ${({ $disabled, $tokens }) =>
        !$disabled &&
        `
        ${interactiveStyles($tokens)}

        &:focus-visible, &[data-focus] {
            box-shadow: none;
        }
    `}

    i {
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;
